import * as Print from 'expo-print';
import * as Sharing from 'expo-sharing';
import { Platform } from 'react-native';

export interface ExportDimension {
  name: string;
  rawScore: number;
  feedback: string;
  evidenceLevel: string;
  riskLevel: string;
  recommendation?: string;
}

export interface ExportRisk {
  category: string;
  description: string;
  severity: string;
  mitigation?: string;
}

export interface ExportDealReadinessDoc {
  name: string;
  critical: boolean;
  present: boolean;
}

export interface ExportScorecardPayload {
  companyName: string;
  tagline?: string;
  description?: string;
  finalScore: number;
  ventureLabel: string;
  ventureScore: number;
  evidenceConfidence: number;
  sourceFlavor?: string;
  riskSummary: { high: number; medium: number; low: number };
  dealReadinessPercent: number;
  dealReadinessPresent: number;
  dealReadinessTotal: number;
  // Narrative
  narrativeOverall: string;
  narrativeStrengths: string;
  narrativeWeaknesses: string;
  // Lists
  recommendations: string[];
  risks: ExportRisk[];
  fundamentalsDims: ExportDimension[];
  growthDims: ExportDimension[];
  ddDocs: ExportDealReadinessDoc[];
}

const escape = (s: string | undefined | null): string =>
  String(s ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');

const verdictText = (score: number): string => {
  if (score >= 80) return 'Highly Investable';
  if (score >= 60) return 'Investment Ready';
  if (score >= 40) return 'Developing';
  return 'Early Stage';
};

const verdictColor = (score: number): string => {
  if (score >= 75) return '#22c55e';
  if (score >= 60) return '#3b82f6';
  if (score >= 40) return '#f59e0b';
  return '#ef4444';
};

const severityColor = (sev: string): string => {
  if (sev === 'high') return '#ef4444';
  if (sev === 'medium') return '#f59e0b';
  return '#22c55e';
};

function dimLabel(score: number): string {
  if (score >= 80) return 'Excellent';
  if (score >= 65) return 'Strong';
  if (score >= 45) return 'Moderate';
  if (score >= 25) return 'Weak';
  return 'Critical';
}

function renderDimensionRow(d: ExportDimension): string {
  const tint = verdictColor(d.rawScore);
  const label = dimLabel(d.rawScore);
  return `
    <div class="dim">
      <div class="dim-head">
        <div class="dim-head-name">${escape(d.name)}</div>
        <div class="dim-head-score">
          <span class="dim-score-badge" style="background:${tint}22; color:${tint};">${escape(label)} (${d.rawScore})</span>
        </div>
      </div>
      <p class="dim-feedback">${escape(d.feedback)}</p>
      <div class="dim-meta">
        <span class="dim-meta-pill"><strong>Evidence:</strong> ${escape(d.evidenceLevel)}</span>
        <span class="dim-meta-pill"><strong>Risk:</strong> ${escape(d.riskLevel)}</span>
      </div>
      ${d.recommendation ? `<div class="dim-rec"><strong>How to improve —</strong> ${escape(d.recommendation)}</div>` : ''}
    </div>
  `;
}

function renderRisk(r: ExportRisk): string {
  const tint = severityColor(r.severity);
  return `
    <div class="risk" style="border-color:${tint};">
      <div class="risk-head">
        <div class="risk-sev" style="color:${tint};">${escape(r.severity.toUpperCase())}</div>
        <div class="risk-cat">${escape(r.category)}</div>
      </div>
      <p class="risk-desc">${escape(r.description)}</p>
      ${r.mitigation ? `
        <div class="risk-mit-block">
          <div class="risk-mit-label">✓ Mitigation</div>
          <div class="risk-mit-text">${escape(r.mitigation)}</div>
        </div>
      ` : ''}
    </div>
  `;
}

function renderDdDoc(d: ExportDealReadinessDoc): string {
  const mark = d.present ? '✓' : '✕';
  const color = d.present ? '#22c55e' : d.critical ? '#ef4444' : '#9ca3af';
  return `
    <li>
      <span class="dd-mark" style="color:${color};">${mark}</span>
      <span class="dd-name">${escape(d.name)}</span>
      ${d.critical && !d.present ? '<span class="dd-tag">CRITICAL</span>' : ''}
    </li>
  `;
}

function confidenceBand(c: number): { label: string; color: string } {
  if (c >= 75) return { label: 'Strong', color: '#22c55e' };
  if (c >= 50) return { label: 'Moderate', color: '#f59e0b' };
  return { label: 'Weak', color: '#ef4444' };
}

function buildHtml(p: ExportScorecardPayload, generatedAt: string): string {
  const tint = verdictColor(p.finalScore);
  const verdict = verdictText(p.finalScore);
  const conf = confidenceBand(p.evidenceConfidence);

  // A4 content width @ 96 DPI minus jsPDF margins (14mm × 2 = ~106px reserved)
  // 210mm − 28mm = 182mm = ~688px → use 680 for safety against scrollbars.
  const contentWidth = 680;

  return `<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8" />
  <title>${escape(p.companyName)} — Inverio Scorecard</title>
  <style>
    @page { size: A4; margin: 14mm; }
    @media print {
      html, body, * { -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; color-adjust: exact !important; }
    }
    * { box-sizing: border-box; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
    html, body { margin: 0; padding: 0; }
    body {
      width: ${contentWidth}px;
      font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Helvetica, Arial, sans-serif;
      color: #1f2937;
      line-height: 1.4;
      font-size: 12px;
      background: #f5f5f7; /* match score page surface.secondary */
    }
    .root { width: ${contentWidth}px; padding: 16px; }

    /* ── Section divider — matches ScoreSectionDivider ──────────── */
    .section-divider {
      display: table; width: 100%;
      margin: 20px 0 12px;
      page-break-after: avoid;
    }
    .section-divider .sd-title {
      display: table-cell;
      vertical-align: middle;
      white-space: nowrap;
      padding-right: 12px;
      color: #1f2937;
      font-size: 12px;
      font-weight: 700;
      letter-spacing: 1.4px;
      text-transform: uppercase;
    }
    .section-divider .sd-rule {
      display: table-cell;
      vertical-align: middle;
      width: 100%;
    }
    .section-divider .sd-rule-inner {
      height: 1px;
      background: #d1d5db;
    }
    .section-divider .sd-trail {
      display: table-cell;
      vertical-align: middle;
      white-space: nowrap;
      padding-left: 12px;
      font-size: 12px;
      font-weight: 700;
    }

    /* ── Inverio Scorecard hero card — matches IRSScoreCard ─────── */
    .scorecard {
      background: #ffffff;
      border: 1px solid #e5e7eb;
      border-radius: 16px;
      padding: 18px;
      page-break-inside: avoid;
      margin-bottom: 4px;
    }
    .sc-head {
      display: table; width: 100%; margin-bottom: 14px;
    }
    .sc-brand {
      display: table-cell;
      font-size: 10px; font-weight: 700;
      letter-spacing: 1.6px; text-transform: uppercase;
      color: #6b7280;
    }
    .sc-name {
      display: table-cell;
      text-align: right;
      font-size: 13px; font-weight: 700;
      color: #111827; letter-spacing: -0.1px;
    }
    .sc-body {
      display: table; width: 100%; table-layout: fixed;
    }
    .sc-col-ring {
      display: table-cell;
      vertical-align: middle;
      width: 110px;
    }
    .ring {
      width: 88px; height: 88px;
      border-radius: 50%;
      border: 8px solid ${tint};
      background: #ffffff;
      text-align: center;
      padding-top: 14px;
      box-sizing: border-box;
    }
    .ring-score { font-size: 24px; font-weight: 700; line-height: 1; color: #111827; letter-spacing: -1px; }
    .ring-label { font-size: 9px; font-weight: 700; color: ${tint}; text-transform: uppercase; letter-spacing: 0.6px; margin-top: 2px; }
    .sc-col-about {
      display: table-cell;
      vertical-align: middle;
      padding: 0 12px;
      border-left: 1px solid #e5e7eb;
      border-right: 1px solid #e5e7eb;
      width: 38%;
    }
    .sc-tagline {
      color: #4b5563;
      font-size: 11px;
      line-height: 1.4;
    }
    .sc-col-subs {
      display: table-cell;
      vertical-align: middle;
      padding-left: 12px;
    }
    .sub-card {
      background: #f5f5f7;
      border: 1px solid #e5e7eb;
      border-radius: 10px;
      padding: 8px 10px;
    }
    .sub-row {
      display: table; width: 100%; padding: 3px 0;
    }
    .sub-lbl {
      display: table-cell;
      color: #4b5563; font-size: 11px;
      width: 56%;
    }
    .sub-val {
      display: table-cell;
      text-align: right;
      font-size: 11px; font-weight: 700; color: #111827;
    }
    .sub-val em { font-style: normal; color: #6b7280; font-weight: 500; font-size: 10px; }
    .sub-val .source-flavor {
      display: block;
      font-weight: 500; color: #6b7280; font-size: 10px;
    }

    /* ── Progression strip ──────────────────────────────────────── */
    .progression {
      display: table; width: 100%;
      margin-top: 14px; padding-top: 12px;
      border-top: 1px solid #e5e7eb;
    }
    .verdict {
      display: inline-block;
      padding: 4px 10px;
      border-radius: 999px;
      background: ${tint}22; color: ${tint};
      font-size: 11px; font-weight: 700;
      letter-spacing: 0.3px;
    }
    .verdict-dot {
      display: inline-block;
      width: 6px; height: 6px;
      border-radius: 50%;
      background: ${tint};
      margin-right: 6px;
      vertical-align: middle;
    }

    /* ── Summary card — narrative + tinted sub-cards ────────────── */
    .summary {
      background: #ffffff;
      border: 1px solid #e5e7eb;
      border-radius: 16px;
      padding: 18px;
      margin-bottom: 4px;
      page-break-inside: avoid;
    }
    .narr-block { margin-bottom: 14px; page-break-inside: avoid; }
    .narr-block:last-of-type { margin-bottom: 0; }
    .narr-label {
      font-size: 9px; font-weight: 700;
      letter-spacing: 0.8px; text-transform: uppercase;
      color: #6b7280; margin-bottom: 4px;
    }
    .narr-icon { color: #4b5563; margin-right: 4px; font-weight: 700; }
    .narr-body { color: #1f2937; font-size: 12px; line-height: 1.5; }

    .subcard {
      background: #f5f5f7;
      border: 1px solid #e5e7eb;
      border-radius: 12px;
      padding: 12px;
      margin-top: 12px;
      page-break-inside: avoid;
    }
    .subcard-recs {
      background: #d1fae5;
      border-color: #6ee7b7;
    }
    .subcard-risks {
      background: #fee2e2;
      border-color: #fca5a5;
    }
    .subcard-head {
      display: table; width: 100%;
      margin-bottom: 8px;
    }
    .subcard-icon {
      display: table-cell;
      width: 28px;
      vertical-align: middle;
      font-size: 14px;
      font-weight: 700;
    }
    .subcard-title {
      display: table-cell;
      vertical-align: middle;
      font-size: 13px; font-weight: 700;
      color: #111827;
    }
    .subcard-count {
      display: table-cell;
      vertical-align: middle;
      text-align: right;
      font-size: 11px; font-weight: 700;
    }

    /* ── Recommendations ────────────────────────────────────────── */
    .rec-row {
      display: table;
      width: 100%;
      padding: 6px 0;
    }
    .rec-num {
      display: table-cell;
      width: 26px;
      vertical-align: top;
    }
    .rec-num-badge {
      display: inline-block;
      width: 20px; height: 20px;
      border-radius: 50%;
      background: #6ee7b7;
      color: #047857;
      font-size: 10px; font-weight: 700;
      text-align: center; line-height: 20px;
    }
    .rec-text {
      display: table-cell;
      vertical-align: top;
      font-size: 12px; line-height: 1.5;
      color: #064e3b;
    }

    /* ── Risk Alerts ────────────────────────────────────────────── */
    .risk {
      background: #ffffff;
      border: 1.5px solid;
      border-radius: 10px;
      padding: 10px 12px;
      margin-bottom: 8px;
      page-break-inside: avoid;
    }
    .risk:last-child { margin-bottom: 0; }
    .risk-head {
      display: table; width: 100%;
      margin-bottom: 4px;
    }
    .risk-sev {
      display: table-cell;
      width: 60px;
      font-size: 9px; font-weight: 700;
      letter-spacing: 0.6px;
    }
    .risk-cat {
      display: table-cell;
      font-size: 11px;
      color: #4b5563;
      font-weight: 600;
    }
    .risk-desc {
      font-size: 12px;
      color: #1f2937;
      margin: 4px 0 0;
      line-height: 1.4;
    }
    .risk-mit-block {
      margin-top: 8px;
      padding-top: 8px;
      border-top: 1px solid #e5e7eb;
    }
    .risk-mit-label {
      font-size: 9px; font-weight: 700;
      letter-spacing: 0.6px;
      color: #047857;
      margin-bottom: 2px;
    }
    .risk-mit-text {
      font-size: 11px;
      color: #374151;
      line-height: 1.4;
    }

    /* ── Dimension Card — matches DimensionCard ─────────────────── */
    .dim {
      background: #ffffff;
      border: 1px solid #e5e7eb;
      border-radius: 12px;
      padding: 14px;
      margin-bottom: 10px;
      page-break-inside: avoid;
    }
    .dim-head {
      display: table; width: 100%;
      margin-bottom: 4px;
    }
    .dim-head-name {
      display: table-cell;
      vertical-align: baseline;
      font-size: 13px; font-weight: 700;
      color: #111827;
    }
    .dim-head-score {
      display: table-cell;
      vertical-align: baseline;
      text-align: right;
      width: 100px;
    }
    .dim-score-badge {
      display: inline-block;
      padding: 3px 10px;
      border-radius: 8px;
      font-size: 12px; font-weight: 700;
    }
    .dim-feedback {
      font-size: 12px;
      color: #374151;
      margin: 4px 0 8px;
      line-height: 1.5;
    }
    .dim-meta {
      font-size: 10px;
      color: #6b7280;
      padding-top: 6px;
      border-top: 1px solid #f3f4f6;
    }
    .dim-meta-pill {
      display: inline-block;
      margin-right: 12px;
    }
    .dim-meta-pill strong { color: #4b5563; font-weight: 600; }
    .dim-strength,
    .dim-weakness,
    .dim-rec {
      font-size: 11px;
      margin-top: 6px;
      padding: 8px 10px;
      border-radius: 8px;
    }
    .dim-strength { background: #d1fae5; color: #064e3b; }
    .dim-weakness { background: #fef3c7; color: #78350f; }
    .dim-rec { background: #dbeafe; color: #1e3a8a; }

    /* ── Deal Readiness ─────────────────────────────────────────── */
    .dd-card {
      background: #fef3c7;
      border: 1px solid #fde68a;
      border-radius: 12px;
      padding: 14px;
      page-break-inside: avoid;
    }
    .dd-head {
      display: table; width: 100%; margin-bottom: 8px;
    }
    .dd-count {
      display: table-cell;
      font-size: 18px; font-weight: 700;
      color: #1f2937;
      letter-spacing: -0.3px;
    }
    .dd-count em { font-style: normal; color: #92400e; font-weight: 500; font-size: 13px; }
    .dd-of {
      display: table-cell;
      text-align: right;
      font-size: 11px;
      color: #92400e;
      font-weight: 500;
      vertical-align: bottom;
    }
    .progress {
      height: 6px;
      background: rgba(255,255,255,0.7);
      border-radius: 3px;
      overflow: hidden;
      margin-bottom: 14px;
    }
    .progress-fill { height: 6px; background: #f59e0b; border-radius: 3px; }
    .dd-section-label {
      font-size: 9px; font-weight: 700;
      letter-spacing: 1px; text-transform: uppercase;
      color: #92400e;
      margin: 10px 0 6px;
    }
    ul.dd-list { list-style: none; padding: 0; margin: 0; }
    ul.dd-list li { padding: 3px 0; font-size: 12px; color: #1f2937; }
    .dd-mark { display: inline-block; width: 16px; font-weight: 700; }
    .dd-mark.present { color: #10b981; }
    .dd-mark.missing-crit { color: #dc2626; }
    .dd-mark.missing-rec { color: #f59e0b; }

    /* ── Header & footer ────────────────────────────────────────── */
    .doc-header {
      padding-bottom: 12px;
      margin-bottom: 16px;
      border-bottom: 2px solid #1f2937;
      page-break-after: avoid;
    }
    .doc-brand { font-size: 10px; font-weight: 700; letter-spacing: 1.6px; text-transform: uppercase; color: #6b7280; }
    .doc-h1 { font-size: 22px; font-weight: 700; color: #111827; letter-spacing: -0.5px; margin: 4px 0 4px; }
    .doc-tagline { color: #4b5563; font-size: 12px; font-style: italic; margin: 0; }
    .doc-meta { font-size: 10px; color: #9ca3af; margin-top: 8px; }

    .footer {
      margin-top: 24px;
      padding-top: 10px;
      border-top: 1px solid #e5e7eb;
      font-size: 9px;
      color: #9ca3af;
      text-align: center;
    }
  </style>
</head>
<body>
<div class="root">
  <div class="doc-header">
    <div class="doc-brand">Inverio Scorecard</div>
    <div class="doc-h1">${escape(p.companyName)}</div>
    ${p.tagline ? `<p class="doc-tagline">${escape(p.tagline)}</p>` : ''}
    <div class="doc-meta">Generated ${escape(generatedAt)}</div>
  </div>

  <!-- ── Inverio Scorecard hero ───────────────────────────────── -->
  <div class="scorecard">
    <div class="sc-head">
      <div class="sc-brand">ⓘ Inverio Scorecard</div>
      <div class="sc-name">${escape(p.companyName)}</div>
    </div>
    <div class="sc-body">
      <div class="sc-col-ring">
        <div class="ring">
          <div class="ring-score">${p.finalScore}</div>
          <div class="ring-label">${escape(p.ventureLabel.toUpperCase())}</div>
        </div>
      </div>
      <div class="sc-col-about">
        <div class="sc-tagline">${escape(p.description ?? p.tagline ?? '')}</div>
      </div>
      <div class="sc-col-subs">
        <div class="sub-card">
          <div class="sub-row">
            <div class="sub-lbl">Venture Quality</div>
            <div class="sub-val" style="color:${tint};">${escape(p.ventureLabel)} <em>(${p.ventureScore})</em></div>
          </div>
          <div class="sub-row">
            <div class="sub-lbl">Evidence</div>
            <div class="sub-val" style="color:${conf.color};">
              ${conf.label} · ${p.evidenceConfidence}%
              ${p.sourceFlavor ? `<span class="source-flavor">${escape(p.sourceFlavor)}</span>` : ''}
            </div>
          </div>
          <div class="sub-row">
            <div class="sub-lbl">Risks</div>
            <div class="sub-val">
              ${p.riskSummary.high > 0 ? `<span style="color:#ef4444;">${p.riskSummary.high} high</span>` : ''}
              ${p.riskSummary.high > 0 && p.riskSummary.medium > 0 ? ' · ' : ''}
              ${p.riskSummary.medium > 0 ? `<span style="color:#f59e0b;">${p.riskSummary.medium} med</span>` : ''}
              ${(p.riskSummary.high === 0 && p.riskSummary.medium === 0) ? `<span style="color:#22c55e;">No risk</span>` : ''}
            </div>
          </div>
          <div class="sub-row">
            <div class="sub-lbl">Deal Readiness</div>
            <div class="sub-val">${p.dealReadinessPercent}% <em>(${p.dealReadinessPresent}/${p.dealReadinessTotal})</em></div>
          </div>
        </div>
      </div>
    </div>
    <div class="progression">
      <span class="verdict"><span class="verdict-dot"></span>${escape(verdict)}</span>
    </div>
  </div>

  <!-- ── Venture Quality divider ──────────────────────────────── -->
  <div class="section-divider">
    <div class="sd-title">Venture Quality</div>
    <div class="sd-rule"><div class="sd-rule-inner"></div></div>
    <div class="sd-trail" style="color:${tint};">${escape(p.ventureLabel)} <span style="color:#6b7280; font-weight:500;">(${p.ventureScore})</span></div>
  </div>

  <!-- ── Summary card — narrative + sub-cards ─────────────────── -->
  <div class="summary">
    <div class="narr-block">
      <div class="narr-label"><span class="narr-icon">✦</span>Overall</div>
      <div class="narr-body">${escape(p.narrativeOverall)}</div>
    </div>
    <div class="narr-block">
      <div class="narr-label" style="color:#22c55e;"><span class="narr-icon">▲</span>Strengths</div>
      <div class="narr-body">${escape(p.narrativeStrengths)}</div>
    </div>
    <div class="narr-block">
      <div class="narr-label" style="color:#f59e0b;"><span class="narr-icon">▼</span>Weaknesses</div>
      <div class="narr-body">${escape(p.narrativeWeaknesses)}</div>
    </div>

    ${p.recommendations.length > 0 ? `
      <div class="subcard subcard-recs">
        <div class="subcard-head">
          <div class="subcard-icon" style="color:#22c55e;">★</div>
          <div class="subcard-title" style="color:#064e3b;">Recommendations</div>
          <div class="subcard-count" style="color:#22c55e;">${p.recommendations.length}</div>
        </div>
        ${p.recommendations.map((r, i) => `
          <div class="rec-row">
            <div class="rec-num"><span class="rec-num-badge">${i + 1}</span></div>
            <div class="rec-text">${escape(r)}</div>
          </div>
        `).join('')}
      </div>
    ` : ''}

    ${p.risks.length > 0 ? `
      <div class="subcard subcard-risks">
        <div class="subcard-head">
          <div class="subcard-icon" style="color:#ef4444;">⚠</div>
          <div class="subcard-title" style="color:#7f1d1d;">Risk Alerts</div>
          <div class="subcard-count" style="color:#ef4444;">${p.risks.length}</div>
        </div>
        ${p.risks.map(renderRisk).join('')}
      </div>
    ` : ''}
  </div>

  <!-- ── Fundamentals dimensions ──────────────────────────────── -->
  <div class="section-divider">
    <div class="sd-title">Fundamentals</div>
    <div class="sd-rule"><div class="sd-rule-inner"></div></div>
  </div>
  ${p.fundamentalsDims.map(renderDimensionRow).join('')}

  <!-- ── Growth & Execution dimensions ────────────────────────── -->
  <div class="section-divider">
    <div class="sd-title">Growth &amp; Execution</div>
    <div class="sd-rule"><div class="sd-rule-inner"></div></div>
  </div>
  ${p.growthDims.map(renderDimensionRow).join('')}

  <!-- ── Deal Readiness ───────────────────────────────────────── -->
  <div class="section-divider">
    <div class="sd-title">Deal Readiness</div>
    <div class="sd-rule"><div class="sd-rule-inner"></div></div>
    <div class="sd-trail" style="color:#92400e;">${p.dealReadinessPresent}/${p.dealReadinessTotal}</div>
  </div>
  <div class="dd-card">
    <div class="dd-head">
      <div class="dd-count">${p.dealReadinessPresent} <em>of ${p.dealReadinessTotal}</em></div>
      <div class="dd-of">documents</div>
    </div>
    <div class="progress"><div class="progress-fill" style="width:${p.dealReadinessPercent}%;"></div></div>
    ${(() => {
      const missingCritical = p.ddDocs.filter((d) => !d.present && d.critical);
      const missingRecommended = p.ddDocs.filter((d) => !d.present && !d.critical);
      const present = p.ddDocs.filter((d) => d.present);
      let html = '';
      if (missingCritical.length > 0) {
        html += `<div class="dd-section-label">Missing Critical</div><ul class="dd-list">${missingCritical.map((d) => `<li><span class="dd-mark missing-crit">✕</span><span>${escape(d.name)}</span></li>`).join('')}</ul>`;
      }
      if (missingRecommended.length > 0) {
        html += `<div class="dd-section-label">Missing Recommended</div><ul class="dd-list">${missingRecommended.map((d) => `<li><span class="dd-mark missing-rec">○</span><span>${escape(d.name)}</span></li>`).join('')}</ul>`;
      }
      if (present.length > 0) {
        html += `<div class="dd-section-label">Present</div><ul class="dd-list">${present.map((d) => `<li><span class="dd-mark present">✓</span><span>${escape(d.name)}</span></li>`).join('')}</ul>`;
      }
      return html;
    })()}
  </div>

  <div class="footer">
    Inverio · Investment Readiness Scorecard · Confidential
  </div>
</div>
</body>
</html>`;
}

function safeFilename(name: string): string {
  return name.trim().replace(/[^a-z0-9]+/gi, '_').replace(/^_|_$/g, '') || 'scorecard';
}

/**
 * Web: render the report as native PDF components via @react-pdf/renderer
 * (vector output — no canvas rasterization, no print dialog), then trigger
 * a direct download via an anchor click.
 */
async function exportPdfOnWeb(payload: ExportScorecardPayload, generatedAt: string): Promise<void> {
  if (typeof document === 'undefined') {
    throw new Error('Web environment unavailable');
  }

  // Lazy-load — @react-pdf/renderer is substantial; only load when needed.
  const [{ pdf }, docModule] = await Promise.all([
    import('@react-pdf/renderer'),
    import('./scorecard-pdf-document'),
  ]);
  const { ScorecardPdfDocument } = docModule;

  const blob = await pdf(<ScorecardPdfDocument payload={payload} generatedAt={generatedAt} />).toBlob();
  const filename = `inverio_scorecard_${safeFilename(payload.companyName)}.pdf`;

  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 60_000);
}

export async function exportScorecardPdf(payload: ExportScorecardPayload): Promise<void> {
  const generatedAt = new Date().toLocaleString();

  if (Platform.OS === 'web') {
    // Web: vector PDF via @react-pdf/renderer, direct download (no print dialog).
    await exportPdfOnWeb(payload, generatedAt);
    return;
  }

  // Native (iOS/Android): expo-print produces a real PDF from the HTML template.
  const html = buildHtml(payload, generatedAt);
  const { uri } = await Print.printToFileAsync({ html, base64: false });
  const canShare = await Sharing.isAvailableAsync();
  if (canShare) {
    await Sharing.shareAsync(uri, {
      UTI: 'com.adobe.pdf',
      mimeType: 'application/pdf',
      dialogTitle: 'Inverio Scorecard',
    });
  } else {
    throw new Error('Sharing is unavailable on this device.');
  }
}
